// Central watchlist for products/software/systems that UC's stack depends on.
// The authoritative list now lives in the backend (`settings.news_watch_keywords`)
// so admins can add/remove terms without a code push — see Settings ▸ Engineering
// ▸ Watchlist. This module keeps a hard-coded FALLBACK so the UI still highlights
// something sensible if the settings request hasn't returned yet.

export const DEFAULT_WATCH_KEYWORDS = [
  "NinjaOne", "ThreatLocker", "Intruder", "Emergent", "1Password", "Guardz",
  "incident.io", "Pingdom", "Cloudflare",
  "Microsoft", "Windows", "Office 365", "Microsoft 365", "M365", "Exchange",
  "Azure", "Entra", "Active Directory", "AD FS", "Windows Server", "Hyper-V",
  "VMware", "vCenter", "ESXi",
  "Cisco", "Meraki", "Fortinet", "FortiGate", "Palo Alto", "OPNsense",
  "Ubiquiti", "UniFi",
  "3CX", "Asterisk", "FreePBX", "SBC", "SIP",
  "React", "Node.js", "FastAPI", "MongoDB", "Postgres", "PostgreSQL",
  "Nginx", "OpenSSH", "OpenSSL",
  "Chrome", "Chromium", "Edge", "Firefox", "macOS", "Linux",
  "Veeam", "S3", "Backblaze",
];

// Retained for backward-compat with earlier imports.
export const WATCH_KEYWORDS = DEFAULT_WATCH_KEYWORDS;

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function buildRegex(keywords) {
  const terms = (keywords || [])
    .map((k) => (k || "").trim())
    .filter(Boolean);
  if (!terms.length) return null;
  return new RegExp(
    `(?<![A-Za-z0-9])(${terms.map(escapeRegex).join("|")})(?![A-Za-z0-9])`,
    "gi",
  );
}

// Cache the compiled regex per keyword-list identity so we don't recompile on
// every card render. Keyword lists are small, so a plain WeakMap keyed on the
// caller-provided array works well when it's the same reference across renders.
const REGEX_CACHE = new WeakMap();

function getRegex(keywords) {
  if (!keywords) return buildRegex(DEFAULT_WATCH_KEYWORDS);
  const cached = REGEX_CACHE.get(keywords);
  if (cached) return cached;
  const re = buildRegex(keywords);
  if (re) REGEX_CACHE.set(keywords, re);
  return re;
}

/**
 * Return the set of canonical watch terms detected in a piece of text.
 * `keywords` — optional custom watchlist (default: DEFAULT_WATCH_KEYWORDS).
 */
export function detectKeywords(fields, keywords) {
  const regex = getRegex(keywords);
  if (!regex) return [];
  const list = keywords || DEFAULT_WATCH_KEYWORDS;
  const hits = new Set();
  for (const raw of Array.isArray(fields) ? fields : [fields]) {
    if (!raw) continue;
    const text = String(raw);
    let m;
    regex.lastIndex = 0;
    while ((m = regex.exec(text)) !== null) {
      const found = m[0];
      // Snap back to the exact input casing so the UI stays consistent.
      const canonical = list.find((w) => w.toLowerCase() === found.toLowerCase()) || found;
      hits.add(canonical);
    }
  }
  return Array.from(hits);
}

export function detectKeywordsForItem(item, keywords) {
  if (!item) return [];
  return detectKeywords(
    [item.title, item.excerpt, item.body_html, item.source, item.cve_id],
    keywords,
  );
}
